import { PRICE_QUOTE_REVIEW_URL } from "../MainAppPanel";
import Rfq from "../models/Rfq";
import PriceQuote, {
  PriceQuoteLineItem,
  PriceQuoteStatus,
} from "../models/PriceQuote";
import { RfqHomework } from "../models/RfqHomework";
import { RfqItem } from "../models/RfqItem";

/** Same-tab + cross-tab signal that work-queue price-quote lists should refetch. */
export const PRICE_QUOTES_CHANGED_EVENT = "procurement:price-quotes-changed";
const PRICE_QUOTES_CHANGED_STORAGE_KEY = "procurement:price-quotes-changed";

/** Notify open work-queue / RFQ views that inbound price quotes changed (e.g. dismissed). */
export function notifyPriceQuotesChanged(): void {
  window.dispatchEvent(new Event(PRICE_QUOTES_CHANGED_EVENT));
  try {
    localStorage.setItem(PRICE_QUOTES_CHANGED_STORAGE_KEY, String(Date.now()));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Subscribe to price-quote list invalidation (same tab + other tabs). Returns unsubscribe. */
export function subscribePriceQuotesChanged(onChange: () => void): () => void {
  const onEvent = () => onChange();
  const onStorage = (event: StorageEvent) => {
    if (event.key === PRICE_QUOTES_CHANGED_STORAGE_KEY) {
      onChange();
    }
  };
  window.addEventListener(PRICE_QUOTES_CHANGED_EVENT, onEvent);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(PRICE_QUOTES_CHANGED_EVENT, onEvent);
    window.removeEventListener("storage", onStorage);
  };
}

/** Pink highlight for homework lines with an inbound vendor price quote (matches IN_PROGRESS status chip). */
export const RECEIVED_PRICE_QUOTE_LINE_HIGHLIGHT = {
  bgcolor: "rgba(255, 105, 180, 0.16)",
  outline: "2px solid #FF69B4",
  outlineOffset: 0,
} as const;

/** Match a price-quote line to a homework line (requestedItemId, then line number). */
export function matchPriceQuoteLineForItem(
  priceQuote: PriceQuote | null | undefined,
  item: RfqItem,
  lineNumber: number
): PriceQuoteLineItem | null {
  if (!priceQuote?.lineItems?.length) return null;
  return (
    priceQuote.lineItems.find((line) => line.requestedItemId === item.id) ??
    priceQuote.lineItems.find((line) => line.lineNumber === lineNumber) ??
    null
  );
}

/** Homework line item ids with a matching inbound price-quote line. */
export function homeworkItemIdsWithReceivedPriceQuote(
  priceQuote: PriceQuote | null | undefined,
  homework: RfqHomework
): number[] {
  if (!priceQuote?.lineItems?.length) return [];
  return homework.items
    .filter((item, index) =>
      Boolean(matchPriceQuoteLineForItem(priceQuote, item, index + 1))
    )
    .map((item) => item.id);
}

/** Work-queue “Price Quote” column values. */
export type WorkQueuePriceQuoteStatus = "" | "Awaiting" | "Received";

function activeQuotesForRfq(
  rfqId: number,
  priceQuotes: PriceQuote[]
): PriceQuote[] {
  return priceQuotes.filter(
    (pq) =>
      pq.rfqId === rfqId &&
      pq.status !== PriceQuoteStatus.DISMISSED &&
      pq.status !== PriceQuoteStatus.UNMATCHED
  );
}

function vendorIdsWithOutboundRfqs(rfq: Rfq): Set<number> {
  const ids = new Set<number>();
  for (const homework of rfq.quotes ?? []) {
    for (const vendorRfq of homework.vendorRfqs ?? []) {
      if (vendorRfq.vendorId != null) {
        ids.add(vendorRfq.vendorId);
      }
    }
  }
  return ids;
}

/**
 * blank — no outbound vendor RFQs and no inbound quotes
 * Awaiting — vendor RFQ(s) sent without a matching inbound price quote
 * Received — at least one matched/pending inbound price quote for this RFQ
 */
export function workQueuePriceQuoteStatus(
  rfq: Rfq,
  priceQuotes: PriceQuote[]
): WorkQueuePriceQuoteStatus {
  const quotes = activeQuotesForRfq(rfq.id, priceQuotes);
  if (quotes.length > 0) {
    return "Received";
  }

  const outboundVendorIds = vendorIdsWithOutboundRfqs(rfq);
  if (outboundVendorIds.size === 0) {
    return "";
  }

  return "Awaiting";
}

/** Newest non-dismissed matched/pending quote for an RFQ (for Review link). */
export function latestReceivedPriceQuote(
  rfqId: number,
  priceQuotes: PriceQuote[]
): PriceQuote | null {
  const quotes = activeQuotesForRfq(rfqId, priceQuotes).slice();
  quotes.sort(
    (a, b) =>
      new Date(b.receivedDateTime).getTime() -
      new Date(a.receivedDateTime).getTime()
  );
  return quotes[0] ?? null;
}

/** Open the full-screen price quote review route in a new browser tab. */
export function openPriceQuoteReviewInNewTab(
  priceQuoteId: number,
  lineItemId?: number
): Window | null {
  let path = PRICE_QUOTE_REVIEW_URL.replace(
    ":priceQuoteId",
    String(priceQuoteId)
  );
  if (lineItemId != null && lineItemId > 0) {
    path += `?lineItemId=${lineItemId}`;
  }
  const url = `${window.location.origin}${path}`;
  // Keep window.opener so dismiss/approve can focus the original tab and close.
  return window.open(url, "_blank");
}

/**
 * Finish a comparison opened in a script-created tab: focus the opener and
 * close this window. Same-tab navigations fall back to `fallback` (e.g. RFQ).
 */
export function closeReviewWindowOrFallback(fallback: () => void): void {
  const opener = window.opener as Window | null;
  if (opener && !opener.closed) {
    try {
      opener.focus();
    } catch {
      /* ignore cross-origin */
    }
    window.close();
    return;
  }

  window.close();
  window.setTimeout(() => {
    if (!window.closed) {
      fallback();
    }
  }, 0);
}

/**
 * After approve: show `url` in the window that opened this comparison, then
 * close this tab. If there is no opener, or it cannot be navigated, run
 * `fallback` in this window (same-tab review → RFQ).
 */
export function openUrlInOpenerOrFallback(
  url: string,
  fallback: () => void
): void {
  const opener = window.opener as Window | null;
  if (opener && !opener.closed) {
    try {
      opener.location.assign(url);
      opener.focus();
      window.close();
      window.setTimeout(() => {
        if (!window.closed) fallback();
      }, 0);
      return;
    } catch {
      /* opener blocked — stay in this window */
    }
  }

  window.close();
  window.setTimeout(() => {
    if (!window.closed) fallback();
  }, 0);
}

export function unmatchedPriceQuoteByEmailId(
  emailId: number,
  priceQuotes: PriceQuote[]
): PriceQuote | undefined {
  return priceQuotes.find(
    (pq) =>
      pq.emailId === emailId && pq.status === PriceQuoteStatus.UNMATCHED
  );
}
